import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {flowRight as compose} from 'lodash';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {withTranslation} from 'react-i18next';
import Screen from '../../../common/components/layout/Screen';
import Metadata from '../../../common/components/Metadata';
import {ContentDiv, HeadDiv, Span} from '../../../common/components/StyledComp';
import {faQuestionCircle, faReceipt} from '@fortawesome/free-solid-svg-icons';
import {withRouter} from 'react-router';
import DatatableLoadMore from '../../../common/components/Datatable/DatatableLoadMore';
import gql from 'graphql-tag';
import DisplayAmount from '../../../common/components/DisplayAmount';
import {dateFormatFR} from '../../../utils';
import DisplayInvoiceStatus from './DisplayInvoiceStatus';
import {COLORS, INVOICE_STATUS} from '../../../common/constants';
import FlexRow from '../../../common/components/layout/FlexRow';
import DownloadInvoice from './DownloadInvoice';
import {MargedDiv} from '../../../common/components/Datatable/DatatableStyles';
import SelectInvoicesStatus from './SelectInvoicesStatus';
import {Col, Container, Row} from 'react-bootstrap';
import SubscriptionManagement from '../../stripe/components/SubscriptionManagement';
import PaymentMethodManagement from '../../stripe/components/PaymentMethodManagement';
import {withLoadedUser} from '../../../login/containers/AuthBase';
import {useQuery} from 'react-apollo';
import HostedInvoiceUrl from './HostedStripeInvoice';
import styled from 'styled-components';

const LinkText = styled.a`
  color: ${COLORS.BLACK};
   &:hover {
    color: ${COLORS.BLACK};
  }
`;

const HelpIconStyle = {
  marginLeft: '10px',
  color: COLORS.BLACK,
};

const GRAPHQL_QUERY_CURRENT_MERCHANT_INVOICES = gql`
  query currentMerchantInvoices($criteria:CriteriaListInput, $additionalCriteria:InvoicesCriteriaListInput) {
    currentUser {
      id
      invoices(criteria: $criteria, additionalCriteria: $additionalCriteria){
        rows{
          id
          amountDue
          amountPaid
          amountRemaining
          created
          currency
          status
          invoicePdf
          hostedInvoiceUrl
          paid
          total
          tax
        }
        id
        hasMore
      }
    }
  }
`;

const GRAPHQL_QUERY_MERCHANT_CURRENT_USAGE_RECORD = gql`
  query currentMerchantSubscriptionUsage{
    currentUser{
      id
      merchant{
        id
        currentBillingSubscriptionUsageRecordSummary {
          id
          totalUsage
        }
      }
    }
  }
`;

const columns = t => [
  {
    Header: t('date'),
    accessor: 'created',
    Cell: ({cell}) => dateFormatFR({date: cell.value}),
  },
  {
    Header: t('status.title'),
    accessor: 'status',
    Cell: ({cell}) => <DisplayInvoiceStatus status={cell.value} />,
  },
  {
    Header: t('amountDue'),
    accessor: 'total',
    Cell: ({cell}) => <DisplayAmount amount={cell.value}/>,
  },
  {
    Header: t('action'),
    Cell: ({cell}) => (
      <FlexRow>
        {cell.value.status !== INVOICE_STATUS.DRAFT && (
          <>
            <DownloadInvoice invoicePdf={cell.value.invoicePdf}/>
            <HostedInvoiceUrl hostedInvoiceUrl={cell.value.hostedInvoiceUrl}/>
          </>
        )}
      </FlexRow>
    ),
    accessor: row => row,
  },
];

const createStatusFilters = status => {
  let filters = {};

  if (status !== 'default' && status !== 'all') {
    filters = {
      status,
      ...filters,
    };
  }

  return filters;
};

const MerchantBillingDetails = ({t, currentUser}) => {

  const [statusFilter, setStatusFilter] = useState('default');
  const {error: errorMerchant, data: dataMerchant} = useQuery(GRAPHQL_QUERY_MERCHANT_CURRENT_USAGE_RECORD);

  const additionalFilters = (
    <MargedDiv>
      <SelectInvoicesStatus value={statusFilter} allowNull onChange={e => setStatusFilter(e.target.value)}/>
    </MargedDiv>
  );

  if (errorMerchant) {
    console.error(errorMerchant);
  }

  const totalUsage =
    dataMerchant &&
    dataMerchant.currentUser &&
    dataMerchant.currentUser.merchant &&
    dataMerchant.currentUser.merchant.currentBillingSubscriptionUsageRecordSummary &&
    dataMerchant.currentUser.merchant.currentBillingSubscriptionUsageRecordSummary.totalUsage ?
      dataMerchant.currentUser.merchant.currentBillingSubscriptionUsageRecordSummary.totalUsage : 0;

  return (
    <Screen
      header={
        <Metadata meta={t('billing')} title={t('billing')}/>
      }
      leftTitle={
        <>
          <FontAwesomeIcon icon={faReceipt} color={'rgb(201,0,95)'}/>
          <Span className="route-name">{t('billing')}</Span>
        </>
      }
      rightTitle={
        <LinkText href={'https://www.cleanbill.fr/wef9enpdmfzpv5ybdp4d/'} target="_blank" rel="noopener noreferrer">
          {t('subscriptionInfos')}
          <FontAwesomeIcon style={HelpIconStyle} icon={faQuestionCircle} size={'lg'}/>
        </LinkText>
      }
    >
      <>
        <Container className="general-container">
          <Row className="info-row">
            <Col lg={12} xl={6}>
              <div className="section-info-user">
                <HeadDiv className="info-header-user">{`${t('subscription')} : `}</HeadDiv>
                <ContentDiv>
                  <SubscriptionManagement userId={currentUser.id} allowCancelSubscription={false} currentUsage={totalUsage}/>
                </ContentDiv>
              </div>
            </Col>
            <Col lg={12} className={'d-xl-none'}>
              <br/>
            </Col>
            <Col lg={12} xl={6}>
              <div className="section-info-user">
                <HeadDiv className="info-header-user">{`${t('payment')} : `}</HeadDiv>
                <ContentDiv>
                  <PaymentMethodManagement userId={currentUser.id}/>
                </ContentDiv>
              </div>
            </Col>
          </Row>
        </Container>

        <DatatableLoadMore
          query={GRAPHQL_QUERY_CURRENT_MERCHANT_INVOICES}
          keyResult="currentUser.invoices"
          columns={columns(t)}
          additionalCriteria={createStatusFilters(statusFilter)}
          additionalFilters={additionalFilters}
        />
      </>
    </Screen>
  );
};

MerchantBillingDetails.propTypes = {
  t: PropTypes.func.isRequired,
  currentUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
};

export default compose(
  withTranslation('billing'),
  withRouter,
  withLoadedUser,
)(MerchantBillingDetails);
